import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  ReturnPolicy,
  ThumbnailClient,
  ThumbnailData,
  ThumbnailResponseState,
  ThumbnailTypes,
} from '@rbx/thumbnails';
import { useTranslation } from '@rbx/intl';
import {
  BrokenImageOutlinedIcon,
  Grid,
  HourglassEmptyIcon,
  NotInterestedIcon,
  Skeleton,
  Typography,
} from '@rbx/ui';
import useThumbnailImageStyles from './ThumbnailImage.styles';
import { ThumbnailImageProps } from './ThumbnailImage';

const useThumbnailImage = ({
  targetId,
  targetType,
  returnPolicy = ReturnPolicy.AutoGenerated,
  isStatusTextShown = false,
  alt,
  fontColor = 'light',
  bottomRightAdornment,
}: ThumbnailImageProps) => {
  const {
    classes: {
      imageStatusContainer,
      statusTextContainer,
      imageWrapper,
      imageContainer,
      image,
      bottomRightAdornmentContainer,
      skeleton,
    },
  } = useThumbnailImageStyles(fontColor !== 'light')();
  const { translate } = useTranslation();
  const [isLoadingThumbnail, setIsLoadingThumbnail] = useState<boolean>(false);
  const [thumbnailImageInfo, setThumbnailImageInfo] = useState<ThumbnailData>();

  const getTargetThumbnailData = useCallback(
    async (
      type: ThumbnailTypes,
      id: number,
      policy: ReturnPolicy,
      isThumbnailRefreshRequired = false,
    ) => {
      if (!id || !type) {
        return;
      }
      setIsLoadingThumbnail(true);
      try {
        const getThumbnailImage = isThumbnailRefreshRequired
          ? ThumbnailClient.reloadThumbnailImage
          : ThumbnailClient.getThumbnailImage;
        const data = await getThumbnailImage(type, id, policy);
        setThumbnailImageInfo(data);
      } catch (e) {
        setThumbnailImageInfo({ state: ThumbnailResponseState.TemporarilyUnavailable });
      } finally {
        setIsLoadingThumbnail(false);
      }
    },
    [],
  );

  const statusContent = useMemo(() => {
    if (isLoadingThumbnail) {
      return <Skeleton classes={{ root: skeleton }} variant='rectangular' animate />;
    }
    let statusText = '';
    let statusIcon = null;
    switch (thumbnailImageInfo?.state) {
      case ThumbnailResponseState.InReview:
        statusText = translate('Label.InReview');
        statusIcon = (
          <HourglassEmptyIcon
            fontSize='large'
            color={fontColor === 'dark' ? undefined : 'secondary'}
          />
        );
        break;
      case ThumbnailResponseState.Blocked:
        statusText = translate('Label.Moderated');
        statusIcon = (
          <NotInterestedIcon
            fontSize='large'
            color={fontColor === 'dark' ? undefined : 'secondary'}
          />
        );
        break;
      case ThumbnailResponseState.Error:
      case ThumbnailResponseState.TemporarilyUnavailable:
        statusText = translate('Label.Unavailable');
        statusIcon = (
          <BrokenImageOutlinedIcon
            fontSize='large'
            color={fontColor === 'dark' ? undefined : 'secondary'}
          />
        );
        break;
      default:
        return null;
    }
    return (
      <Grid
        container
        classes={{ root: imageStatusContainer }}
        justifyContent='center'
        alignItems='center'>
        {isStatusTextShown ? (
          <div>
            {statusIcon}
            <Typography variant='smallLabel1' classes={{ root: statusTextContainer }}>
              {statusText}
            </Typography>
          </div>
        ) : (
          statusIcon
        )}
      </Grid>
    );
  }, [
    isLoadingThumbnail,
    thumbnailImageInfo?.state,
    imageStatusContainer,
    isStatusTextShown,
    statusTextContainer,
    skeleton,
    translate,
    fontColor,
  ]);

  useEffect(() => {
    getTargetThumbnailData(targetType, targetId, returnPolicy);
  }, [targetId, targetType, returnPolicy, getTargetThumbnailData]);

  const thumbnailImage = (
    <Grid item container XSmall={12} justifyContent='center' alignItems='center'>
      <div className={imageContainer}>
        {thumbnailImageInfo?.state === ThumbnailResponseState.Completed ? (
          <img className={image} src={thumbnailImageInfo?.imageUrl} alt={alt} />
        ) : (
          <div className={imageWrapper}>{statusContent}</div>
        )}
        {bottomRightAdornment ? (
          <div className={bottomRightAdornmentContainer}>{bottomRightAdornment}</div>
        ) : null}
      </div>
    </Grid>
  );

  const refreshThumbnail = useCallback(async () => {
    return getTargetThumbnailData(targetType, targetId, returnPolicy, true);
  }, [getTargetThumbnailData, targetType, targetId, returnPolicy]);

  return { thumbnailImage, thumbnailData: thumbnailImageInfo, refreshThumbnail };
};

export default useThumbnailImage;
